import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { Indikation, IndikationEvent } from '../../../warehouse/material/artikel-indikation/indikation';
import { KieneTableService } from '../../kiene-table/kiene-table.service';

/** Platzhalter für die Option "keine Diagnose" (bei abgabebeleg_dosierungstext). */
export const KEINE_DIAGNOSE: Indikation = {
    indikation_id: null,
    diagnose: '- keine Diagnose -',
    artikel_id: 0,
    tierart_id: 0,
    artikel: '',
    tierart: '',
    erstellt: null,
    erstellt_von: null,
    editiert: null,
    editiert_von: null,
    sortierung: 0,
} as Indikation;

export function isKeineDiagnose(indikation: Indikation | null | undefined): boolean {
    return indikation != null && indikation.indikation_id == null && indikation.diagnose === 'keine Diagnose';
}

@Component({
    selector: 'kiene-diagnose-selector',
    templateUrl: './diagnose-selector.component.html',
    styleUrls: ['./diagnose-selector.component.css'],
})
export class DiagnoseSelectorComponent implements OnInit, OnChanges {
    @ViewChild('diagnoseSelect') diagnoseSelect: MatSelect;

    diagnoseCtrl = new UntypedFormControl();

    indikationen: Indikation[] = [];

    @Input('disabled') disabled = false;
    @Input('required') required = false;
    @Input('kundeMandantId') kundeMandantId: number;
    @Input() showKeineDiagnoseOption = false;

    @Output('change') change = new EventEmitter<IndikationEvent>();

    constructor(
        private api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
    ) {}

    ngOnInit(): void {
        if (this.required) {
            this.diagnoseCtrl.setValidators([Validators.required]);
        }
        this.syncDisabledState();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['required']) {
            this.applyRequiredValidator();
        }
        if (changes['disabled']) {
            this.syncDisabledState();
        }
    }

    private syncDisabledState(): void {
        if (this.disabled) {
            this.diagnoseCtrl.disable({ emitEvent: false });
        } else {
            this.diagnoseCtrl.enable({ emitEvent: false });
        }
    }

    private applyRequiredValidator(): void {
        if (this.required) {
            this.diagnoseCtrl.setValidators([Validators.required]);
        } else {
            this.diagnoseCtrl.clearValidators();
        }
        this.diagnoseCtrl.updateValueAndValidity({ emitEvent: false });
    }

    public isValid(): boolean {
        if (!this.required) {
            return true;
        } else {
            return this.diagnoseCtrl.valid;
        }
    }

    loadDiagnosen(
        materialId: number,
        tierartId: number,
        anwendungId: number,
        emit: boolean,
        ladePosition: boolean,
        callback?: () => void,
    ) {
        let params = new HttpParams();
        params = params.set('artikel_id', materialId.toString());
        if (tierartId) {
            params = params.set('tierart_id', tierartId.toString());
        }
        if (anwendungId) {
            params = params.set('anwendung_id', anwendungId.toString());
        }

        let headers;
        if (this.kundeMandantId !== undefined) {
            headers = { 'MANDANTID-KUNDE': this.kundeMandantId.toString() };
        }
        this.api.get(this.apiBaseUrl + 'indikation/read.php', params, headers).subscribe({
            next: response => {
                this.indikationen = response.records;
                if (this.showKeineDiagnoseOption) {
                    this.indikationen = [KEINE_DIAGNOSE, ...this.indikationen];
                }
                if (this.indikationen.length === 1) {
                    this.diagnoseCtrl.setValue(this.indikationen[0]);
                    if (ladePosition) {
                        this.change.emit(new IndikationEvent(this.indikationen[0], { ladePosition: true }));
                    } else if (emit) {
                        this.change.emit(new IndikationEvent(this.indikationen[0]));
                    }
                } else {
                    if (emit && !ladePosition) {
                        this.setFocus();
                    }
                }
                callback?.();
            },
            error: () => {},
        });
    }

    indikationSelected(event: MatSelectChange) {
        this.change.emit(new IndikationEvent(event.value));
    }

    indikationOpenedChange() {}

    indikationComparator(a: Indikation | null, b: Indikation | null): boolean {
        if (!a && !b) {
            return true;
        }
        if (!a || !b) {
            return false;
        }
        const aId = a.indikation_id;
        const bId = b.indikation_id;
        if (aId == null && bId == null) {
            return a.diagnose === b.diagnose;
        }
        return Number(aId) === Number(bId);
    }

    public setKeineDiagnose(): void {
        this.diagnoseCtrl.setValue(KEINE_DIAGNOSE);
    }

    public clear(): void {
        this.indikationen = [];
        this.diagnoseCtrl.setValue(null);
        this.diagnoseSelect.close();
        this.diagnoseCtrl.markAsUntouched();
        this.change.emit(new IndikationEvent(null));
    }

    public close(): void {
        this.diagnoseSelect.close();
    }

    public setFocus(): void {
        setTimeout(() => {
            this.diagnoseSelect?.focus();
            this.openPanel();
        }, 50);
    }

    public openPanel() {
        this.diagnoseSelect.open();
    }

    public setDiagnose(diagnose: Indikation, anwendungId: number, emit: boolean, ladePosition: boolean) {
        this.diagnoseCtrl.setValue(diagnose, { emitEvent: emit });

        this.loadDiagnosen(diagnose.artikel_id, diagnose.tierart_id, anwendungId, emit, ladePosition);
    }

    public getIndikation(): Indikation | undefined {
        return this.diagnoseCtrl?.value;
    }

    public setRequired(required: boolean): void {
        this.required = required;
        if (required) {
            this.diagnoseCtrl.setValidators([Validators.required]);
        } else {
            this.diagnoseCtrl.clearValidators();
        }
        this.diagnoseCtrl.updateValueAndValidity({ emitEvent: false });
    }
}
